import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import { ApiGet, ApiPost, ApiPut, ApiDelete } from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import { DataTable } from "../components/DataTable";
import { usePermissions } from "../hooks/usePermissions";
import { useImageCrop } from "../hooks";
import { IMAGE_OR_PDF, validateUploadFile } from "../utils/fileValidation";
import { getUploadErrorMessage } from "../helpers/uploadErrorMessage";
import { PageHeader } from "../components/PageHeader";
import { DocumentFileField } from "../components/DocumentFileField";
import { buildDocumentColumns } from "./document/documentColumns";
import { useContentLanguage } from "../hooks/useContentLanguage";
import { LanguageTabs } from "../components/LanguageTabs/LanguageTabs";

export default function Document() {
  const { canCreate, canEdit, canDelete } = usePermissions("/document");
  const [documentData, setDocumentData] = useState<any>({});
  const [addDocument, setAddDocument] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllDocument, setGetAllDocument] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [documentIdForEdit, setDocumentIdForEdit] = useState<any>();
  const [documentIdForDelete, setDocumentIdForDelete] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);

  const [lang, setLang] = useContentLanguage();

  const imageCropGer = useImageCrop(IMAGE_OR_PDF);
  const imageCropEn = useImageCrop(IMAGE_OR_PDF);
  const [removeDocumentEn, setRemoveDocumentEn] = useState(false);
  const [removeDocumentGer, setRemoveDocumentGer] = useState(false);

  const isImageFile = (file: File | string | null | undefined): boolean => {
    if (!file) return false;
    if (file instanceof File) return !file.name?.toLowerCase().endsWith(".pdf");
    return typeof file === "string" && !file.toLowerCase().endsWith(".pdf");
  };

  useEffect(() => {
    getAllDocumentData();
  }, [page, countPerPage, search]);

  const addDocumentModelClose = () => {
    setAddDocument(false);
    setIsEditApi(false);
    setErrors({});
    setDocumentData({});
    imageCropGer.reset();
    imageCropEn.reset();
    setRemoveDocumentEn(false);
    setRemoveDocumentGer(false);
  };

  const handleRemoveFile = (lang: "en" | "ger") => {
    const key = lang === "en" ? "file" : "file_ger";
    const crop = lang === "en" ? imageCropEn : imageCropGer;
    const setRemove = lang === "en" ? setRemoveDocumentEn : setRemoveDocumentGer;
    const current = documentData?.[key];
    const isExistingUrl = typeof current === "string" && current.length > 0;
    setDocumentData({ ...documentData, [key]: "" });
    crop.reset();
    setErrors({ ...errors, [key]: "" });
    if (isExistingUrl) setRemove(true);
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setDocumentData({ ...documentData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleFileChange = (e: any, lang: "en" | "ger") => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate size/type up front. PDFs skip the cropper (which is what runs
    // this check for images), so without this they reached the server
    // unvalidated and a too-large marketing PDF only failed at upload time.
    const validation = validateUploadFile(file, IMAGE_OR_PDF);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    const key = lang === "en" ? "file" : "file_ger";
    const crop = lang === "en" ? imageCropEn : imageCropGer;
    const setRemove = lang === "en" ? setRemoveDocumentEn : setRemoveDocumentGer;
    setDocumentData({ ...documentData, [key]: file });
    setErrors({ ...errors, [key]: "" });
    setRemove(false);
    if (isImageFile(file)) {
      crop.handleImageUpload(e);
    } else {
      crop.reset();
    }
  };

  const formValidation = () => {
    let formIsValid = true;
    const errs: any = {};
    if (!documentData?.documentName?.trim()) {
      formIsValid = false;
      errs["documentName"] = "*Please enter the English title";
    }
    if (!documentData?.documentName_ger?.trim()) {
      formIsValid = false;
      errs["documentName_ger"] = "*Bitte geben Sie den deutschen Titel ein";
    }
    if (!isEditApi && !documentData?.file) {
      formIsValid = false;
      errs["file"] = "*Please select an English file";
    }
    if (!isEditApi && !documentData?.file_ger) {
      formIsValid = false;
      errs["file_ger"] = "*Bitte eine deutsche Datei ausw\u00e4hlen";
    }
    setErrors(errs);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllDocumentData = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`document/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllDocument(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const buildFormData = async (): Promise<FormData> => {
    const formData = new FormData();
    formData.append("documentName", documentData?.documentName);
    formData.append("documentName_ger", documentData?.documentName_ger);

    // EN file
    if (imageCropEn.imageToUpload && isImageFile(imageCropEn.imageToUpload)) {
      const croppedFile = await imageCropEn.getCroppedFile();
      if (croppedFile) formData.append("document", croppedFile);
    } else if (documentData?.file instanceof File) {
      formData.append("document", documentData.file);
    }

    // DE file
    if (imageCropGer.imageToUpload && isImageFile(imageCropGer.imageToUpload)) {
      const croppedFile = await imageCropGer.getCroppedFile();
      if (croppedFile) formData.append("document_ger", croppedFile);
    } else if (documentData?.file_ger instanceof File) {
      formData.append("document_ger", documentData.file_ger);
    }

    return formData;
  };

  const addDocumentData = async () => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = await buildFormData();
        await ApiPost(`document/create`, formData);
        toast.success("Datei erfolgreich hinzuf\u00fcgen");
        setAddDocument(false);
        setIsEditApi(false);
        getAllDocumentData();
        setDocumentData({});
        imageCropEn.reset();
        imageCropGer.reset();
      } catch (err: any) {
        toast.error(getUploadErrorMessage(err));
      } finally {
        setLoader(false);
      }
    }
  };

  const editDocumentData = async () => {
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = await buildFormData();
        if (removeDocumentEn) formData.append("removeDocument", "true");
        if (removeDocumentGer) formData.append("removeDocument_ger", "true");
        await ApiPut(`document/update?id=${documentIdForEdit}`, formData);
        toast.success("Datei erfolgreich geupdated");
        getAllDocumentData();
        setAddDocument(false);
        setIsEditApi(false);
        setDocumentData({});
        imageCropEn.reset();
        imageCropGer.reset();
        setRemoveDocumentEn(false);
        setRemoveDocumentGer(false);
      } catch (err: any) {
        toast.error(getUploadErrorMessage(err));
      } finally {
        setLoader(false);
      }
    }
  };

  const deleteDocumentData = async () => {
    await ApiDelete(`document/delete?id=${documentIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Dokument erfolgreich gel\u00f6scht");
          setShow(false);
          setPage(1);
          getAllDocumentData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const columns = useMemo(() => buildDocumentColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setAddDocument(true);
      setDocumentIdForEdit(row?._id);
      setDocumentData({
        documentName: row?.documentName,
        documentName_ger: row?.documentName_ger,
        file: row?.document?.[0],
        file_ger: row?.document_ger?.[0],
      });
    },
    onDelete: (row: any) => {
      setDocumentIdForDelete(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Objekt Dokumente"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setDocumentData({});
            setErrors({});
            imageCropEn.reset();
            imageCropGer.reset();
            setAddDocument(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllDocument || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          L&ouml;schen best&auml;tigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diese Dokument l&ouml;schen m&ouml;chten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteDocumentData()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addDocument && (
        <Dialog fullScreen open={addDocument}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => addDocumentModelClose()}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Dual-language column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Deutsch
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    English
                  </Typography>
                </Box>
              </Box>

              {/* Titel row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Titel
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="documentName_ger"
                    name="documentName_ger"
                    value={documentData?.documentName_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["documentName_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="documentName"
                    name="documentName"
                    value={documentData?.documentName || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["documentName"]}
                  </Typography>
                </Box>
              </Box>

              {/* Datei row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Datei
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <DocumentFileField
                    inputId="fileUploadGer"
                    inputName="document_ger"
                    imageCrop={imageCropGer}
                    file={documentData?.file_ger}
                    error={errors["file_ger"]}
                    onFileChange={(e) => handleFileChange(e, "ger")}
                    onRemove={() => handleRemoveFile("ger")}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <DocumentFileField
                    inputId="fileUploadEn"
                    inputName="document"
                    imageCrop={imageCropEn}
                    file={documentData?.file}
                    error={errors["file"]}
                    onFileChange={(e) => handleFileChange(e, "en")}
                    onRemove={() => handleRemoveFile("en")}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {loader ? (
                <Button variant="contained" type="button" disabled>
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                  <CircularProgress size={20} sx={{ mx: 3, color: "white" }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    isEditApi ? editDocumentData() : addDocumentData();
                  }}
                >
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                </Button>
              )}
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
